import { Link } from "react-router-dom";
import {
  Facebook,
  Instagram,
  MessageCircle,
  Mail,
  Phone,
  MapPin,
  Twitter,
  Youtube,
  Linkedin,
  Send,
  Globe,
} from "lucide-react";
import { useSettings } from "@/hooks/useSettings";

type FooterLink = { label: string; url: string };
type FooterSection = { enabled?: boolean; heading: string; links: FooterLink[] };
type ImageItem = { name: string; image_url: string };
type SocialItem = { platform: string; url: string };

type FooterConfig = {
  logo_url?: string;
  brand_description?: string;
  sections?: {
    information?: FooterSection;
    quick?: FooterSection;
    profile?: FooterSection;
  };
  contact?: {
    enabled?: boolean;
    heading?: string;
    phone?: string;
    email?: string;
    address?: string;
  };
  payments?: { enabled?: boolean; heading?: string; items?: ImageItem[] };
  couriers?: { enabled?: boolean; heading?: string; items?: ImageItem[] };
  socials?: { enabled?: boolean; items?: SocialItem[] };
  copyright?: string;
};

const isExternal = (url: string) =>
  /^(https?:)?\/\//i.test(url) || url.startsWith("mailto:") || url.startsWith("tel:");

const SOCIAL_ICONS: Record<string, any> = {
  facebook: Facebook,
  instagram: Instagram,
  whatsapp: MessageCircle,
  messenger: MessageCircle,
  twitter: Twitter,
  x: Twitter,
  youtube: Youtube,
  linkedin: Linkedin,
  telegram: Send,
};

const FooterLinkItem = ({ link }: { link: FooterLink }) => {
  const className =
    "text-sm text-muted-foreground hover:text-foreground transition-smooth";
  return isExternal(link.url) ? (
    <a href={link.url} target="_blank" rel="noopener noreferrer" className={className}>
      {link.label}
    </a>
  ) : (
    <Link to={link.url} className={className}>
      {link.label}
    </Link>
  );
};

const LinkSection = ({ section }: { section?: FooterSection }) => {
  if (!section || section.enabled === false) return null;
  const links = (section.links || []).filter((l) => l.label && l.url);
  if (!links.length) return null;
  return (
    <div className="space-y-3">
      <h4 className="text-sm font-semibold text-foreground">{section.heading}</h4>
      <ul className="space-y-2">
        {links.map((l, i) => (
          <li key={i}><FooterLinkItem link={l} /></li>
        ))}
      </ul>
    </div>
  );
};

const DEFAULT_CONFIG: FooterConfig = {
  brand_description: "Honest products, honest prices. Delivered across Bangladesh.",
  sections: {
    information: {
      enabled: true,
      heading: "Information",
      links: [
        { label: "Delivery", url: "/delivery" },
        { label: "Privacy Policy", url: "/privacy" },
        { label: "Return Policy", url: "/return-policy" },
      ],
    },
    quick: {
      enabled: true,
      heading: "Quick Links",
      links: [
        { label: "Home", url: "/" },
        { label: "Contact", url: "/contact" },
        { label: "Wishlist", url: "/wishlist" },
      ],
    },
    profile: {
      enabled: true,
      heading: "Profile",
      links: [
        { label: "Login", url: "/auth" },
        { label: "Register", url: "/auth" },
        { label: "Order Tracking", url: "/orders" },
      ],
    },
  },
  contact: { enabled: true, heading: "Contact Us" },
  payments: { enabled: true, heading: "Payment Methods", items: [] },
  couriers: { enabled: true, heading: "Shipping Partners", items: [] },
  socials: { enabled: true, items: [] },
};

export const StoreFooter = () => {
  const settings = useSettings();
  const storeName = settings.store_name || "Rupshoili";
  const cfg: FooterConfig =
    settings.footer_config && typeof settings.footer_config === "object"
      ? { ...DEFAULT_CONFIG, ...settings.footer_config }
      : DEFAULT_CONFIG;

  const logoUrl = cfg.logo_url || settings.logo_url || "";
  const description = cfg.brand_description || settings.footer_about || DEFAULT_CONFIG.brand_description!;
  const contact = cfg.contact || {};
  const payments = (cfg.payments?.items || []).filter((p) => p.image_url);
  const couriers = (cfg.couriers?.items || []).filter((p) => p.image_url);
  const socials = (cfg.socials?.items || []).filter((s) => s.url && s.platform);
  const copyright =
    cfg.copyright ||
    settings.copyright_text ||
    `© ${new Date().getFullYear()} ${storeName}. All rights reserved.`;

  return (
    <footer className="mt-12 border-t border-border/60 bg-secondary/30">
      <div className="container py-8">
        <div className="grid gap-8 md:gap-6 grid-cols-2 md:grid-cols-3 lg:grid-cols-5">
          {/* Brand */}
          <div className="col-span-2 md:col-span-3 lg:col-span-1 space-y-3">
            <Link to="/" className="inline-flex items-center gap-2">
              {logoUrl ? (
                <img src={logoUrl} alt={storeName} className="h-9 w-auto object-contain" />
              ) : (
                <span className="font-display text-xl font-bold tracking-tight">
                  {storeName}
                </span>
              )}
            </Link>
            <p className="text-sm text-muted-foreground leading-relaxed max-w-xs">
              {description}
            </p>
            {cfg.socials?.enabled !== false && socials.length > 0 && (
              <div className="flex flex-wrap items-center gap-2 pt-1">
                {socials.map((s, i) => {
                  const Icon = SOCIAL_ICONS[s.platform.toLowerCase()] || Globe;
                  return (
                    <a
                      key={i}
                      href={s.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={s.platform}
                      className="h-8 w-8 grid place-items-center rounded-full bg-background border border-border/60 text-muted-foreground hover:text-primary hover:border-primary/40 transition-smooth"
                    >
                      <Icon className="h-3.5 w-3.5" />
                    </a>
                  );
                })}
              </div>
            )}
          </div>

          <LinkSection section={cfg.sections?.information} />
          <LinkSection section={cfg.sections?.quick} />
          <LinkSection section={cfg.sections?.profile} />

          {/* Contact */}
          {contact.enabled !== false && (contact.phone || contact.email || contact.address) && (
            <div className="space-y-3">
              <h4 className="text-sm font-semibold text-foreground">{contact.heading || "Contact Us"}</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                {contact.phone && (
                  <li className="flex items-center gap-2">
                    <Phone className="h-3.5 w-3.5 shrink-0 text-primary/70" />
                    <a href={`tel:${contact.phone}`} className="hover:text-foreground transition-smooth">
                      {contact.phone}
                    </a>
                  </li>
                )}
                {contact.email && (
                  <li className="flex items-center gap-2">
                    <Mail className="h-3.5 w-3.5 shrink-0 text-primary/70" />
                    <a href={`mailto:${contact.email}`} className="hover:text-foreground transition-smooth truncate">
                      {contact.email}
                    </a>
                  </li>
                )}
                {contact.address && (
                  <li className="flex items-start gap-2">
                    <MapPin className="h-3.5 w-3.5 shrink-0 mt-0.5 text-primary/70" />
                    <span>{contact.address}</span>
                  </li>
                )}
              </ul>
            </div>
          )}
        </div>

        {/* Payments + Couriers */}
        {((cfg.payments?.enabled !== false && payments.length > 0) ||
          (cfg.couriers?.enabled !== false && couriers.length > 0)) && (
          <div className="mt-8 pt-6 border-t border-border/60 grid gap-6 md:grid-cols-2">
            {cfg.payments?.enabled !== false && payments.length > 0 && (
              <div>
                <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">
                  {cfg.payments?.heading || "Payment Methods"}
                </h4>
                <div className="flex flex-wrap items-center gap-2">
                  {payments.map((p, i) => (
                    <div
                      key={i}
                      className="h-9 px-2 rounded-md bg-background border border-border/60 grid place-items-center"
                      title={p.name}
                    >
                      <img src={p.image_url} alt={p.name} className="h-6 w-auto object-contain" />
                    </div>
                  ))}
                </div>
              </div>
            )}
            {cfg.couriers?.enabled !== false && couriers.length > 0 && (
              <div>
                <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">
                  {cfg.couriers?.heading || "Shipping Partners"}
                </h4>
                <div className="flex flex-wrap items-center gap-2">
                  {couriers.map((p, i) => (
                    <div
                      key={i}
                      className="h-9 px-2 rounded-md bg-background border border-border/60 grid place-items-center"
                      title={p.name}
                    >
                      <img src={p.image_url} alt={p.name} className="h-6 w-auto object-contain" />
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Bottom bar */}
        <div className="mt-6 pt-4 border-t border-border/60 flex flex-col md:flex-row items-center justify-between gap-3">
          <p className="text-xs text-muted-foreground text-center md:text-left">
            {copyright}
          </p>
        </div>
        <p className="mt-2 text-[11px] text-muted-foreground/80 italic font-display tracking-wide text-center">
          Developed by{" "}
          <span className="text-primary font-semibold not-italic">
            Muhammad Shohag
          </span>
        </p>
      </div>
    </footer>
  );
};
